/**
 * useBulkPdfJob
 * -----------------------------------------------------------------------------
 * React hook + helper for bulk-PDF download with automatic
 *   • inline streaming for small batches (<=50 records), and
 *   • background job + progress polling + auto-download for larger batches.
 *
 * Also exports a small <BulkPdfProgress /> component for the UI.
 *
 * Usage:
 *
 *   const { start, progress, status, error, downloadUrl, reset } = useBulkPdfJob();
 *
 *   start({
 *     asyncEndpoint: `${API}/api/events/${eventId}/badges/bulk-pdf/async`,
 *     inlineEndpoint: `${API}/api/events/${eventId}/badges/bulk-pdf`,
 *     body: { badges_per_page: 8, page_size: 'A4' },
 *     recordCount: selected.length,          // used to decide inline vs async
 *     filename: `badges-${eventId}.pdf`,
 *   });
 */
import { useCallback, useRef, useState } from 'react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const ASYNC_THRESHOLD = 50;

const authHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useBulkPdfJob() {
  const [status, setStatus] = useState('idle');   // idle | preparing | running | done | failed
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadedBlob, setDownloadedBlob] = useState(null);
  const [filename, setFilename] = useState(null);
  const [jobId, setJobId] = useState(null);
  const pollRef = useRef(null);

  const reset = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setStatus('idle');
    setProgress(0);
    setError(null);
    setDownloadUrl(null);
    setDownloadedBlob(null);
    setFilename(null);
    setJobId(null);
  }, []);

  const finishWithBlob = useCallback((blob, fname) => {
    setDownloadedBlob(blob);
    setFilename(fname || 'download.pdf');
    setStatus('done');
    setProgress(100);
    setDownloadUrl(URL.createObjectURL(blob));
  }, []);

  /**
   * Runs the small-batch inline flow (single POST, downloads the blob directly).
   */
  const runInline = useCallback(async (endpoint, body, fname, autoDownload) => {
    setStatus('running');
    setProgress(10);
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body || {}),
    });
    setProgress(80);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Request failed (${res.status})`);
    }
    const blob = await res.blob();
    finishWithBlob(blob, fname);
    if (autoDownload) triggerBrowserDownload(blob, fname);
  }, [finishWithBlob]);

  /**
   * Runs the async flow — kick off job, poll status, fetch PDF when done.
   */
  const runAsync = useCallback(async (endpoint, body, fname, autoDownload) => {
    setStatus('preparing');
    setProgress(0);

    const kickoff = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body || {}),
    });
    if (!kickoff.ok) {
      const err = await kickoff.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to start job (${kickoff.status})`);
    }
    const { job_id } = await kickoff.json();
    setJobId(job_id);
    setStatus('running');

    // Poll every 1.5s
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/bulk-jobs/${job_id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) throw new Error(`Status poll failed (${r.status})`);
        const s = await r.json();
        setProgress(s.progress || 0);

        if (s.status === 'done') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Download bytes
          const dl = await fetch(`${API}/api/bulk-jobs/${job_id}/download`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (!dl.ok) throw new Error(`Download failed (${dl.status})`);
          const blob = await dl.blob();
          finishWithBlob(blob, s.filename || fname);
          if (autoDownload) triggerBrowserDownload(blob, s.filename || fname);
        } else if (s.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStatus('failed');
          setError(s.error || 'Job failed');
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setStatus('failed');
        setError(e.message || 'Polling failed');
      }
    };
    pollRef.current = setInterval(poll, 1500);
    poll();
  }, [finishWithBlob]);

  const start = useCallback(async ({ asyncEndpoint, inlineEndpoint, body, recordCount = 0, filename: fname, forceAsync = false, autoDownload = true }) => {
    reset();
    setStatus('preparing');
    try {
      const useAsync = forceAsync || (recordCount > ASYNC_THRESHOLD && asyncEndpoint);
      if (useAsync) {
        await runAsync(asyncEndpoint, body, fname, autoDownload);
      } else {
        await runInline(inlineEndpoint, body, fname, autoDownload);
      }
    } catch (e) {
      setStatus('failed');
      setError(e.message || 'Bulk PDF generation failed');
    }
  }, [reset, runAsync, runInline]);

  const manualDownload = useCallback(() => {
    if (downloadedBlob && filename) triggerBrowserDownload(downloadedBlob, filename);
  }, [downloadedBlob, filename]);

  return { start, reset, manualDownload, status, progress, error, downloadUrl, filename, jobId };
}

// ---------------------------------------------------------------------------
// UI: tiny inline progress component consumers can render
// ---------------------------------------------------------------------------
import React from 'react';
import { Loader2, CheckCircle2, AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const BulkPdfProgress = ({ status, progress, error, filename, onDownload, onReset, className = '' }) => {
  if (status === 'idle') return null;

  if (status === 'preparing' || status === 'running') {
    return (
      <div className={`flex items-center gap-3 p-3 border rounded-md bg-slate-50 text-sm ${className}`} data-testid="bulk-pdf-progress">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium">Generating documents…</div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <div className="h-1.5 bg-slate-200 rounded-full flex-1 overflow-hidden max-w-[220px]">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress || 5}%` }} />
            </div>
            <span>{progress || 0}%</span>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className={`flex items-center gap-3 p-3 border border-green-200 rounded-md bg-green-50 text-sm ${className}`} data-testid="bulk-pdf-progress-done">
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-green-900">Ready</div>
          <div className="text-xs text-green-700 truncate">{filename}</div>
        </div>
        <Button size="sm" variant="outline" onClick={onDownload} data-testid="bulk-pdf-download-btn">
          <Download className="w-3.5 h-3.5 mr-1" />
          Download PDF
        </Button>
        {onReset && (
          <Button size="sm" variant="ghost" onClick={onReset}>Close</Button>
        )}
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className={`flex items-center gap-3 p-3 border border-red-200 rounded-md bg-red-50 text-sm ${className}`} data-testid="bulk-pdf-progress-failed">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-red-900">Failed</div>
          <div className="text-xs text-red-700 truncate">{error || 'Please try again'}</div>
        </div>
        {onReset && (
          <Button size="sm" variant="outline" onClick={onReset}>Dismiss</Button>
        )}
      </div>
    );
  }

  return null;
};
